import { Card } from '@/types/Card';
import { getCardsByIds, cards } from './cards';
import { CHANNEL_KEYS, CHANNEL_NAMES } from '../constants/keys';

export type CardChannel = {
  id: string;
  name: string;
  description: string;
  cards: Card[];
  icon?: React.ReactNode;
};

// Helper function to find cards that have features for a specific channel
const findCardsByChannel = (channelName: string): string[] => {
  return Object.values(cards)
    .filter(
      card =>
        card.features?.some(feature =>
          feature.condition?.toLowerCase().includes(channelName.toLowerCase())
        ) ||
        card.features?.some(feature =>
          feature.channel?.toLowerCase().includes(channelName.toLowerCase())
        )
    )
    .map(card => card.key as string);
};

// Get card data for each channel using the centralized getCardsByIds function
const getChannelCards = (channelId: string) => {
  return getCardsByIds(channelCardIds[channelId as keyof typeof channelCardIds]);
};

// Helper function to determine the appropriate description suffix based on channel key
const getDescriptionSuffix = (channelKey: string): string => {
  // Special cases for certain channels
  if (
    channelKey === CHANNEL_KEYS.ONLINE_SHOPPING ||
    channelKey === CHANNEL_KEYS.SHOPEE ||
    channelKey === CHANNEL_KEYS.MOMO ||
    channelKey === CHANNEL_KEYS.PCHOME ||
    channelKey === CHANNEL_KEYS.COUPANG
  ) {
    return '網購優惠';
  } else if (channelKey === CHANNEL_KEYS.KKDAY || channelKey === CHANNEL_KEYS.KLOOK) {
    return '旅遊優惠';
  } else if (channelKey === CHANNEL_KEYS.AGODA || channelKey === CHANNEL_KEYS.BOOKING) {
    return '訂房優惠';
  } else if (channelKey === CHANNEL_KEYS.TRAVEL || channelKey === CHANNEL_KEYS.ENTERTAINMENT) {
    return '消費優惠';
  } else if (
    channelKey === CHANNEL_KEYS.MOBILE_PAYMENT ||
    channelKey === CHANNEL_KEYS.LINE_PAY ||
    channelKey === CHANNEL_KEYS.APPLE_PAY ||
    channelKey === CHANNEL_KEYS.PXPAYPLUS ||
    channelKey === CHANNEL_KEYS.DIGITAL_BANKING ||
    channelKey === CHANNEL_KEYS.JKOPAY ||
    channelKey === CHANNEL_KEYS.TAIWAN_PAY ||
    channelKey === CHANNEL_KEYS.TAISHIN_PAY
  ) {
    return '支付優惠';
  } else if (channelKey === CHANNEL_KEYS.INSURANCE) {
    return '生活開銷優惠';
  } else if (channelKey === CHANNEL_KEYS.STREAMING) {
    return '影音平台優惠';
  }

  // Default description suffix for most channels
  return '消費優惠';
};

// Centralized definition of channel card IDs using constants
// Generate channelCardIds by mapping through all CHANNEL_KEYS
const channelCardIds = Object.entries(CHANNEL_KEYS).reduce(
  (acc, [_, channelKey]) => {
    // Use the channel key to find the corresponding name and search for cards
    // Skip keys that don't have corresponding names yet
    if (CHANNEL_NAMES[channelKey as keyof typeof CHANNEL_NAMES]) {
      acc[channelKey] = findCardsByChannel(CHANNEL_NAMES[channelKey as keyof typeof CHANNEL_NAMES]);
    } else {
      // Default to empty array for new keys
      acc[channelKey] = [];
    }
    return acc;
  },
  {} as Record<string, string[]>
);

// Create organized channel groups programmatically
export const cardChannels: CardChannel[] = Object.values(CHANNEL_KEYS)
  .filter(channelKey => CHANNEL_NAMES[channelKey as keyof typeof CHANNEL_NAMES])
  .map(channelKey => {
    const name = CHANNEL_NAMES[channelKey as keyof typeof CHANNEL_NAMES];
    const suffix = getDescriptionSuffix(channelKey);

    // For special cases like PUBLIC_TRANSIT and STREAMING, use the full suffix as the description
    const description = channelKey === CHANNEL_KEYS.STREAMING ? suffix : `${name} ${suffix}`;

    return {
      id: channelKey,
      name,
      description,
      cards: getChannelCards(channelKey),
    };
  });
